import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CardActions, Collapse, Grid, TextareaAutosize, TextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSelector, useDispatch } from 'react-redux';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';
import { ICSPNote, IUpdateComplianceStatusNotes } from 'store/redux-store/placement-details/types';
import { useParams } from 'react-router-dom';
import { selectUser } from 'oidc/user.selectors';
import { selectComplianceStatusProgressNotes } from 'store/redux-store/placement-details/selectors';
import { Controller, FormProvider, useForm } from 'react-hook-form';

const useStyles = makeStyles()({
  cardActions: {
    minHeight: '104px',
    padding: '20px 24px 10px',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
  },
  actionControls: {
    padding: '0px 24px 10px',
  },
});

const ComplianceStatusProgressdNoteInput = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { expanded, isClearForm, editableNoteDetails, isEditNotes } = useSelector(selectComplianceStatusProgressNotes);
  const dispatch = useDispatch();
  const params = useParams<{ placementId: string }>();
  const userInfo = useSelector(selectUser);
  const formMethods = useForm({ defaultValues: { note: '' } });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty, isSubmitted },
  } = formMethods;

  const handleFocus = () => {
    dispatch(placementDetailsAction.toggleComplianceStatusNotesDrawerNoteTextboxExpanded(true));
  };

  const handleBlur = () => {
    if (!isDirty) dispatch(placementDetailsAction.toggleComplianceStatusNotesDrawerNoteTextboxExpanded(false));
  };

  const handleCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (getValues('note') !== '') {
      event.stopPropagation();
      dispatch(placementDetailsAction.toggleComplianceStatusNotesDrawerNoteCancel(true));
    }
  };

  const handleOnSave = (data: any) => {
    const newNoteObj: ICSPNote = {
      placementId: parseInt(params.placementId),
      notes: data.note,
      updatedByUserId: userInfo.userInfo.employeeId,
      updatedByName: userInfo.userInfo.firstName + ' ' + userInfo.userInfo.lastName,
    };
    dispatch(placementDetailsAction.toggleComplianceStatusNotesDrawerNoteTextboxExpanded(false));
    dispatch(placementDetailsAction.saveComplianceStatusNote({ newNote: newNoteObj }));
    dispatch(placementDetailsAction.toggleComplianceStatusNotesDrawerClearForm(false));
  };

  const handleNotesEdit = (data: any) => {
    const editNoteObj: IUpdateComplianceStatusNotes = {
      placementId: parseInt(params.placementId),
      notes: data.note,
      existingNoteTimeStamp: editableNoteDetails.lastUpdatedDate,
      updatedByUserId: userInfo.userInfo.employeeId,
      updatedByName: editableNoteDetails.userName,
    };
    dispatch(placementDetailsAction.toggleComplianceStatusNotesDrawerNoteTextboxExpanded(false));
    dispatch(placementDetailsAction.updateComplianceStatusNotes(editNoteObj));
    dispatch(placementDetailsAction.toggleComplianceStatusNotesDrawerClearForm(false));
    dispatch(placementDetailsAction.setEditComplianceStatusNotes(false));
  }

  useEffect(() => {
    if (isEditNotes) {
      reset({ note: editableNoteDetails.notes })
    } else {
      reset({ note: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableNoteDetails, isEditNotes])

  useEffect(() => {
    if (isClearForm) {
      if (isEditNotes) {
        reset({ note: '' })
        dispatch(placementDetailsAction.setEditComplianceStatusNotes(false));
      } else {
        reset();
      }
      dispatch(placementDetailsAction.toggleComplianceStatusNotesDrawerClearForm(false));
    }
  }, [dispatch, isClearForm, reset, isEditNotes])

  return (
    <>
      <FormProvider {...formMethods}>
        <form id="csp-note-form" autoComplete="off" noValidate={true}>
          <CardActions className={classes.cardActions}>
            <Grid container>
              <Grid item md={12} sx={{ pb: 1 }}>
                <Controller
                  name="note"
                  control={control}
                  render={({ field: { onChange, value, ...rest } }) => (
                    <TextField
                      {...rest}
                      autoFocus={expanded}
                      multiline
                      label={t('placement.profile.complianceStatusProgress.noteTextField')}
                      placeholder={t('placement.profile.complianceStatusProgress.notePlaceholder')}
                      variant="filled"
                      fullWidth
                      value={value}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onChange={onChange}
                      InputProps={{
                        inputComponent: TextareaAutosize,
                        inputProps: {
                          style: {
                            minHeight: expanded || value.trim().length > 0 ? '100px' : '25px',
                            maxHeight: '100px',
                            overflow: 'auto',
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardActions>
          <Collapse in={expanded} timeout="auto">
            <Grid container className={classes.actionControls}>
              <Grid item alignContent="end" md={12} justifyContent="end" display="flex" flexDirection="row">
                <Button variant="contained" size="large" onClick={handleCancel} type="button">
                  {'Cancel'}
                </Button>
                <Button
                  variant="contained"
                  type="button"
                  size="large"
                  sx={{ ml: 1 }}
                  disabled={!isDirty || isSubmitted}
                  form="csp-note-form"
                  onClick={handleSubmit(d => isEditNotes ? handleNotesEdit(d) : handleOnSave(d))}
                >
                  {'Save'}
                </Button>
              </Grid>
            </Grid>
          </Collapse>
        </form>
      </FormProvider>
    </>
  );
};

export default ComplianceStatusProgressdNoteInput;
